import React, { useState, useEffect, useMemo, useRef } from "react";
import { useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import { useEffectiveRolePath } from "../../hooks/useEffectiveRolePath";
import { PAGINATION_PAGE_SIZE_OPTIONS } from "../../config/pagination";
import {
  Box,
  Typography,
  Card,
  CardContent,
  Button,
  TextField,
  Grid,
  Chip,
  Avatar,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  FormControl,
  FormHelperText,
  InputLabel,
  Select,
  MenuItem,
  InputAdornment,
  Tab,
  Tabs,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Alert,
  Divider,
  LinearProgress,
  Paper,
  CircularProgress,
  Snackbar,
  Autocomplete,
  Checkbox,
} from "@mui/material";
import { DataGrid } from "@mui/x-data-grid";
import {
  Add,
  Edit,
  Delete,
  Search,
  Visibility,
  Close,
  Quiz,
  School,
  Download,
  Upload,
  Grade,
  Assessment,
  CheckCircle,
  Cancel as CancelIcon,
  ConfirmationNumber,
} from "@mui/icons-material";
import { DatePicker, TimePicker } from "@mui/x-date-pickers";
import { useFormik } from "formik";
import * as Yup from "yup";
import dayjs from "dayjs";
import api from "../../services/api";
import courseService from "../../services/courseService";
import departmentService from "../../services/departmentService";
import assignmentService from "../../services/assignmentService";
import examManagementService from "../../services/examManagementService";
import instituteService from "../../services/instituteService";
import PermissionGate from "../../components/common/PermissionGate";
import { HALL_TICKET_PERMISSIONS } from "../../utils/hallTicketPermissions";
import RecordMetadataSection from "../../components/common/RecordMetadataSection";

// Cache last fetched exams so data shows instantly when reopening the page
let examListCache = { exams: [], totalExams: 0 };

// Normalize API exam row to table shape (API uses Class/Subject with capital letter, examDate, startTime/endTime)
const parseSelectedStudentsLen = (row) => {
  const raw = row?.selectedStudents ?? row?.SelectedStudents;
  if (raw == null) return 0;
  if (Array.isArray(raw)) return raw.filter((x) => x != null && x !== "").length;
  if (typeof raw === "string") {
    const t = raw.trim();
    if (!t || t === "null") return 0;
    try {
      const p = JSON.parse(t);
      return Array.isArray(p) ? p.filter((x) => x != null && x !== "").length : 0;
    } catch {
      return 0;
    }
  }
  return 0;
};

const normalizeExamRow = (row) => {
  const subjectName = row.Subject?.name ?? row.subject?.name ?? row.subjectName ?? row.subject ?? "";
  const className = row.Class?.name ?? row.class?.name ?? row.className ?? row.class ?? "";
  const classSection = row.Class?.section ?? row.class?.section ?? row.classSection ?? row.section ?? "";
  const classDisplay =
    className && classSection
      ? `${className}-${classSection}`
      : className || classSection || "";
  let dateStr = row.examDate ?? row.date ?? row.scheduledAt ?? "";
  if (dateStr && typeof dateStr === "string" && dateStr.includes("T")) dateStr = dateStr.split("T")[0];
  const start = row.startTime ?? row.start ?? "";
  const end = row.endTime ?? row.end ?? "";
  const timeStr = [start, end].filter(Boolean).length ? `${start} - ${end}` : "";
  const total = row.totalMarks ?? row.marks ?? 0;
  const pass = row.passingMarks ?? row.passMarks ?? 0;
  // API returns "midTerm" etc – keep canonical value for saves; display lowercase in chips
  const rawExamType = row.examType ?? row.type ?? "quiz";
  const type = String(rawExamType).toLowerCase().replace(/\s/g, "");
  const statusVal = (row.status ?? "upcoming").toLowerCase();
  const selectedLen = parseSelectedStudentsLen(row);
  const marked =
    Number(row.markedCount ?? row.marked ?? 0) ||
    0;
  const totalStudents = Number(row.studentsCount ?? row.totalStudents ?? row.enrolledCount ?? 0) || 0;
  return {
    ...row,
    id: row.id,
    name: row.name ?? row.title ?? "",
    subject: subjectName,
    class: classDisplay || className,
    classSection,
    date: dateStr,
    time: timeStr,
    totalMarks: total,
    passingMarks: pass,
    examType: rawExamType,
    examTypeDisplay: type,
    status: statusVal,
    createdBy: row.createdBy ?? "",
    studentsCount: totalStudents,
    markedCount: marked,
  };
};

const sortExamsWithUpcomingFirst = (rows) => {
  return [...rows].sort((a, b) => {
    const aUpcoming = (a.status || "").toLowerCase() === "upcoming";
    const bUpcoming = (b.status || "").toLowerCase() === "upcoming";
    if (aUpcoming && !bUpcoming) return -1;
    if (!aUpcoming && bUpcoming) return 1;
    return 0;
  });
};

const ExamManagement = () => {
  const { user } = useSelector((state) => state.auth);
  const navigate = useNavigate();
  const { rolePath } = useEffectiveRolePath();
  const [activeTab, setActiveTab] = useState(0);
  const [searchQuery, setSearchQuery] = useState("");
  const [createDialogOpen, setCreateDialogOpen] = useState(false);
  const [markDialogOpen, setMarkDialogOpen] = useState(false);
  const [viewDialogOpen, setViewDialogOpen] = useState(false);
  const [selectedExam, setSelectedExam] = useState(null);
  const [examDialogMode, setExamDialogMode] = useState("create"); // 'create' | 'edit'
  const [editingExamId, setEditingExamId] = useState(null);
  const [subjectOptions, setSubjectOptions] = useState([]);
  const [classOptions, setClassOptions] = useState([]);
  
  // Department, Course, Class, Subject dropdown states
  const [departmentOptions, setDepartmentOptions] = useState([]);
  const [courseOptions, setCourseOptions] = useState([]);
  const [departmentLoading, setDepartmentLoading] = useState(false);
  const [courseLoading, setCourseLoading] = useState(false);
  const [classLoading, setClassLoading] = useState(false);
  const [subjectLoading, setSubjectLoading] = useState(false);
  const [studentOptions, setStudentOptions] = useState([]);
  const [studentLoading, setStudentLoading] = useState(false);

  // Listing from API – hydrate from cache when available so data shows instantly on open
  const [exams, setExams] = useState(() => (examListCache.exams?.length ? [...examListCache.exams] : []));
  const [examsLoading, setExamsLoading] = useState(() => !examListCache.exams?.length);
  const [examsError, setExamsError] = useState(null);
  const [paginationModel, setPaginationModel] = useState({ page: 0, pageSize: 10 });
  const [totalExams, setTotalExams] = useState(0);
  const [subjectIdFilter, setSubjectIdFilter] = useState("");
  const [classIdFilter, setClassIdFilter] = useState("");
  const [departmentIdFilter, setDepartmentIdFilter] = useState("");
  const [courseIdFilter, setCourseIdFilter] = useState("");
  const [statusUpdateId, setStatusUpdateId] = useState(null); // id of exam whose status is being updated
  const [snackbar, setSnackbar] = useState({ open: false, message: "", severity: "success" });
  const [statusConfirmOpen, setStatusConfirmOpen] = useState(false);
  const [statusConfirmPayload, setStatusConfirmPayload] = useState({ examId: null, examName: "", action: null }); // action: 'completed' | 'cancelled'
  const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false);
  const [examToDelete, setExamToDelete] = useState({ id: null, name: "" });
  const [deleteLoading, setDeleteLoading] = useState(false);
  const importFileInputRef = React.useRef(null);

  // Banner: teacher's assignable subjects and classes (from API)
  const [bannerAssignments, setBannerAssignments] = useState({
    subjectNames: [],
    classNames: [],
    loading: true,
  });

  // Form initial values and validation for Create/Edit Exam
  const EXAM_FORM_MIN_MARKS = 10;
  const EXAM_FORM_MAX_MARKS = 100;
  const EXAM_NAME_MIN = 3;
  const EXAM_NAME_MAX = 100;
  const EXAM_INSTRUCTION_MIN = 3;
  const EXAM_INSTRUCTION_MAX = 100;

  const parseTimeToMinutes = (value) => {
    if (!value) return null;

    // Handle dayjs values from MUI pickers
    if (dayjs.isDayjs(value)) {
      const h = value.hour();
      const m = value.minute();
      if (Number.isNaN(h) || Number.isNaN(m)) return null;
      return h * 60 + m;
    }

    // Handle native Date
    if (value instanceof Date) {
      const h = value.getHours();
      const m = value.getMinutes();
      if (Number.isNaN(h) || Number.isNaN(m)) return null;
      return h * 60 + m;
    }

    const raw = String(value).trim();
    if (!raw) return null;

    let timePart = raw.toUpperCase();
    let ampm = null;

    // Extract AM/PM if present (e.g. "12:05 AM", "12:05AM")
    const ampmMatch = timePart.match(/\s*(AM|PM)$/);
    if (ampmMatch) {
      ampm = ampmMatch[1];
      timePart = timePart.replace(/\s*(AM|PM)$/, "").trim();
    }

    const timePieces = timePart.split(":");
    if (timePieces.length < 2) return null;

    let hours = parseInt(timePieces[0], 10);
    let minutes = parseInt(timePieces[1], 10);

    if (Number.isNaN(hours) || Number.isNaN(minutes)) return null;

    if (ampm) {
      if (hours === 12) {
        hours = ampm === "AM" ? 0 : 12;
      } else if (ampm === "PM") {
        hours += 12;
      }
    }

    if (hours < 0 || hours > 23 || minutes < 0 || minutes > 59) return null;

    return hours * 60 + minutes;
  };

  const defaultExamFormValuesRef = useRef({
    name: "",
    departmentId: "",
    courseId: "",
    subjectId: "",
    classId: "",
    examType: "",
    totalMarks: "",
    passingMarks: "",
    examDate: null,
    startTime: null,
    endTime: null,
    instruction: "",
    selectedStudents: [],
  });
  const defaultExamFormValues = defaultExamFormValuesRef.current;
  const examFormValidationSchema = Yup.object({
    name: Yup.string()
      .required("Exam name is required")
      .trim()
      .min(EXAM_NAME_MIN, `Exam name must be at least ${EXAM_NAME_MIN} characters`)
      .max(EXAM_NAME_MAX, `Exam name must be at most ${EXAM_NAME_MAX} characters`),
    departmentId: Yup.mixed().required("Department is required"),
    courseId: Yup.mixed().required("Course is required"),
    subjectId: Yup.mixed().required("Subject is required"),
    classId: Yup.mixed().required("Class is required"),
    examType: Yup.string().required("Exam type is required"),
    totalMarks: Yup.number()
      .transform((v) => (v === "" || v === null || v === undefined ? undefined : Number(v)))
      .required("Total marks is required")
      .min(EXAM_FORM_MIN_MARKS, `Total marks must be between ${EXAM_FORM_MIN_MARKS} and ${EXAM_FORM_MAX_MARKS}`)
      .max(EXAM_FORM_MAX_MARKS, `Total marks must be between ${EXAM_FORM_MIN_MARKS} and ${EXAM_FORM_MAX_MARKS}`)
      .integer("Must be a whole number"),
    passingMarks: Yup.number()
      .transform((v) => (v === "" || v === null || v === undefined ? undefined : Number(v)))
      .required("Passing marks is required")
      .min(0, "Passing marks cannot be negative")
      .when("totalMarks", (totalMarks, schema) =>
        schema.max(
          Number(totalMarks) || EXAM_FORM_MAX_MARKS,
          "Passing marks cannot exceed total marks"
        )
      )
      .integer("Must be a whole number"),
    examDate: Yup.mixed()
      .required("Required, please fill this first")
      .nullable()
      .test("valid-date", "Please enter a valid exam date", (value) => !value || dayjs(value).isValid()),
    startTime: Yup.mixed()
      .required("Required, please fill this first")
      .nullable()
      .test(
        "valid-time",
        "Please enter a valid start time in HH:MM AM/PM format",
        (value) => !value || parseTimeToMinutes(value) !== null
      ),
    endTime: Yup.mixed()
      .required("Required, please fill this first")
      .nullable()
      .test(
        "valid-time",
        "Please enter a valid end time in HH:MM AM/PM format",
        (value) => !value || parseTimeToMinutes(value) !== null
      )
      .test("after-start", "End time must be greater than start time.", function (value) {
        const { startTime } = this.parent;
        if (!value || !startTime) return true;

        const endMinutes = parseTimeToMinutes(value);
        const startMinutes = parseTimeToMinutes(startTime);

        if (endMinutes === null || startMinutes === null) {
          // Let the "valid-time" tests handle invalid formats
          return true;
        }

        return endMinutes > startMinutes;
      }),
    instruction: Yup.string()
      .trim()
      .test("min-if-present", `Instructions must be at least ${EXAM_INSTRUCTION_MIN} characters`, (v) => !v || String(v).trim().length >= EXAM_INSTRUCTION_MIN)
      .max(EXAM_INSTRUCTION_MAX, `Instructions must be at most ${EXAM_INSTRUCTION_MAX} characters`),
    selectedStudents: Yup.array().of(Yup.number()).optional(),
  });

  const mapExamToFormValues = (exam) => {
    let dateVal = null;
    if (exam.examDate) {
      const dateStr = String(exam.examDate).trim();
      const parsed = dateStr.includes("-") ? dayjs(dateStr) : dayjs(exam.examDate);
      dateVal = parsed.isValid() ? parsed.toDate() : null;
    }
    let startVal = null;
    if (exam.startTime) {
      const timeStr = String(exam.startTime).trim();
      const parsed = timeStr.length <= 8 ? dayjs(`2000-01-01 ${timeStr}`) : dayjs(exam.startTime);
      startVal = parsed.isValid() ? parsed.toDate() : null;
    }
    let endVal = null;
    if (exam.endTime) {
      const timeStr = String(exam.endTime).trim();
      const parsed = timeStr.length <= 8 ? dayjs(`2000-01-01 ${timeStr}`) : dayjs(exam.endTime);
      endVal = parsed.isValid() ? parsed.toDate() : null;
    }
    const rawT = exam.examType ?? exam.type;
    const tm = exam.totalMarks ?? exam.marks;
    const pm = exam.passingMarks ?? exam.passMarks;
    return {
      name: exam.name ?? "",
      departmentId: exam.departmentId ?? "",
      courseId: exam.courseId ?? "",
      subjectId: exam.subjectId ?? "",
      classId: exam.classId ?? "",
      examType: rawT == null || String(rawT).trim() === "" ? "" : (String(rawT).toLowerCase().replace(/\s/g, "") === "midterm" ? "midTerm" : String(rawT)),
      totalMarks: tm == null || tm === "" ? "" : tm,
      passingMarks: pm == null || pm === "" ? "" : pm,
      examDate: dateVal,
      startTime: startVal,
      endTime: endVal,
      instruction: exam.instruction ?? "",
      selectedStudents: exam.selectedStudents ?? [],
    };
  };

  // Handle cascading dropdowns
  const handleDepartmentChange = (value) => {
    examFormik.setFieldValue("departmentId", value, false);
    examFormik.setFieldValue("courseId", "", false);
    examFormik.setFieldValue("classId", "", false);
    examFormik.setFieldValue("subjectId", "", false);
    examFormik.setFieldValue("selectedStudents", [], false);
    setCourseOptions([]);
    setClassOptions([]);
    setSubjectOptions([]);
    setStudentOptions([]);
    if (String(value || "").trim() && examFormik.errors?.departmentId) examFormik.setFieldError("departmentId", undefined);
    if (examFormik.errors?.courseId) examFormik.setFieldError("courseId", undefined);
    if (examFormik.errors?.classId) examFormik.setFieldError("classId", undefined);
    if (examFormik.errors?.subjectId) examFormik.setFieldError("subjectId", undefined);
  };

  const handleCourseChange = (value) => {
    examFormik.setFieldValue("courseId", value, false);
    examFormik.setFieldValue("classId", "", false);
    examFormik.setFieldValue("subjectId", "", false);
    examFormik.setFieldValue("selectedStudents", [], false);
    setClassOptions([]);
    setSubjectOptions([]);
    setStudentOptions([]);
    if (String(value || "").trim() && examFormik.errors?.courseId) examFormik.setFieldError("courseId", undefined);
    if (examFormik.errors?.classId) examFormik.setFieldError("classId", undefined);
    if (examFormik.errors?.subjectId) examFormik.setFieldError("subjectId", undefined);
  };

  const handleClassChange = (value) => {
    examFormik.setFieldValue("classId", value, false);
    examFormik.setFieldValue("subjectId", "", false);
    examFormik.setFieldValue("selectedStudents", [], false);
    setSubjectOptions([]);
    setStudentOptions([]);
    if (String(value || "").trim() && examFormik.errors?.classId) examFormik.setFieldError("classId", undefined);
    if (examFormik.errors?.subjectId) examFormik.setFieldError("subjectId", undefined);
  };

  const handleSubjectChange = (value) => {
    examFormik.setFieldValue("subjectId", value, false);
    if (String(value || "").trim() && examFormik.errors?.subjectId) examFormik.setFieldError("subjectId", undefined);
  };

  const examFormik = useFormik({
    initialValues: defaultExamFormValues,
    validationSchema: examFormValidationSchema,
    enableReinitialize: false,
    validateOnMount: false,
    validateOnChange: false,
    validateOnBlur: false,
    onSubmit: async (values) => {
      const examDateStr = values.examDate && dayjs(values.examDate).isValid()
        ? dayjs(values.examDate).format("YYYY-MM-DD")
        : "";
      const startTimeStr = values.startTime && dayjs(values.startTime).isValid()
        ? dayjs(values.startTime).format("HH:mm:ss")
        : "";
      const endTimeStr = values.endTime && dayjs(values.endTime).isValid()
        ? dayjs(values.endTime).format("HH:mm:ss")
        : "";
      const payload = {
        name: values.name.trim(),
        examType: values.examType,
        totalMarks: Number(values.totalMarks),
        passingMarks: Number(values.passingMarks),
        examDate: examDateStr,
        startTime: startTimeStr,
        endTime: endTimeStr,
        instruction: (values.instruction || "").trim(),
        departmentId: Number(values.departmentId),
        courseId: Number(values.courseId),
        subjectId: Number(values.subjectId),
        classId: Number(values.classId),
        selectedStudents: values.selectedStudents,
      };
      try {
        if (examDialogMode === "edit" && editingExamId) {
          await api.put("/exam/update", payload, { params: { id: editingExamId } });
          setSnackbar({ open: true, message: "Exam updated successfully", severity: "success" });
        } else {
          await api.post("/exam/create", payload);
          setSnackbar({ open: true, message: "Exam created successfully", severity: "success" });
        }
        setCreateDialogOpen(false);
        examFormik.resetForm({ values: { ...defaultExamFormValuesRef.current } });
        setEditingExamId(null);
        setExamDialogMode("create");
        // Refetch exam list
        const res = await api.get("/exam/all", { params: examQueryParams });
        const raw = res?.data?.data?.exams ?? res?.data?.exams ?? [];
        const list = Array.isArray(raw) ? raw : [];
        setExams(sortExamsWithUpcomingFirst(list.map(normalizeExamRow)));
        const pagination = res?.data?.data?.pagination ?? res?.data?.pagination ?? {};
        setTotalExams(pagination.totalItems ?? pagination.total ?? list.length);
      } catch (err) {
        const msg = err?.response?.data?.message ?? err?.message ?? "Request failed";
        examFormik.setStatus(msg);
        setSnackbar({ open: true, message: msg, severity: "error" });
      }
    },
  });
  const examFormikRef = useRef(examFormik);
  examFormikRef.current = examFormik;

  const handleExamNameChange = (e) => {
    const value = e.target.value.slice(0, EXAM_NAME_MAX);
    examFormik.setFieldValue("name", value, false);

    // Clear stale "required" error instantly when user types a non-empty value.
    if (String(value || "").trim().length > 0 && examFormik.errors?.name) {
      examFormik.setFieldError("name", undefined);
    }
  };

  // Fetch departments
  useEffect(() => {
    let isMounted = true;
    const fetchDepartments = async () => {
      setDepartmentLoading(true);
      try {
        const response = await departmentService.getDepartmentNames();
        const departmentsData = response?.data?.data || response?.data || [];
        if (!isMounted) return;
        setDepartmentOptions(Array.isArray(departmentsData) ? departmentsData : []);
      } catch (error) {
        if (isMounted) {
          console.error("Failed to fetch departments:", error);
          setDepartmentOptions([]);
        }
      } finally {
        if (isMounted) {
          setDepartmentLoading(false);
        }
      }
    };
    fetchDepartments();
    return () => { isMounted = false; };
  }, []);

  // Fetch teacher's assignable subjects and classes for the info banner (API or fallback from dept/course/class APIs)
  useEffect(() => {
    let isMounted = true;
    const normalizeName = (item, nameKeys = ["name", "subjectName", "className", "label"]) => {
      if (!item || typeof item === "string") return item || "";
      for (const k of nameKeys) {
        if (item[k] && typeof item[k] === "string") return item[k];
      }
      return "";
    };

    const fetchBannerAssignments = async () => {
      const done = (subjectNames, classNames) => {
        if (!isMounted) return;
        const uniqS = [...new Set(subjectNames)];
        const uniqC = [...new Set(classNames)];
        setBannerAssignments({ subjectNames: uniqS, classNames: uniqC, loading: false });
      };

      try {
        const user = JSON.parse(localStorage.getItem("user") || "{}");
        const teacherId = user?.id ?? user?.userId ?? user?.teacherId;

        if (teacherId) {
          try {
            const response = await examManagementService.getTeacherAssignments(teacherId);
            const data = response?.data?.data ?? response?.data ?? response;
            const subjectNames = [];
            const classNames = [];
            const rawSubjects = data?.subjects ?? data?.subjectNames ?? data?.Subjects ?? [];
            const rawClasses = data?.classes ?? data?.classNames ?? data?.Classes ?? [];

            if (Array.isArray(rawSubjects)) {
              rawSubjects.forEach((s) => {
                const name = normalizeName(s, ["name", "subjectName", "label"]);
                if (name) subjectNames.push(name);
              });
            }
            if (Array.isArray(rawClasses)) {
              rawClasses.forEach((c) => {
                const name = normalizeName(c, ["name", "className", "label"]);
                if (name) classNames.push(name);
              });
            }
            if (subjectNames.length > 0 || classNames.length > 0) {
              done(subjectNames, classNames);
              return;
            }
            if (Array.isArray(data)) {
              data.forEach((item) => {
                const sub = item?.subject ?? item?.Subject;
                const cls = item?.class ?? item?.Class;
                const sn = sub ? normalizeName(sub, ["name", "subjectName", "label"]) : "";
                const cn = cls ? normalizeName(cls, ["name", "className", "label"]) : "";
                if (sn) subjectNames.push(sn);
                if (cn) classNames.push(cn);
              });
              if (subjectNames.length > 0 || classNames.length > 0) {
                done(subjectNames, classNames);
                return;
              }
            }
          } catch (_) {
            // Fall through to fallback
          }
        }
        done([], []);
      } catch (err) {
        if (isMounted) {
          console.error("Failed to fetch banner assignments:", err);
          setBannerAssignments((p) => ({ ...p, subjectNames: [], classNames: [], loading: false }));
        }
      }
    };
    fetchBannerAssignments();
  }, []);

  // Fetch courses based on department
  useEffect(() => {
    let isMounted = true;
    const fetchCourses = async () => {
      if (!examFormik.values.departmentId) {
        setCourseOptions((prev) => (prev.length === 0 ? prev : []));
        setCourseLoading(false);
        return;
      }
      setCourseLoading(true);
      try {
        const params = {
          id: examFormik.values.departmentId,
          departmentId: examFormik.values.departmentId,
        };
        const response = await assignmentService.getAssignmentCourseNames(params);
        const coursesDataRaw = response?.data?.data || response?.data || [];
        const coursesArray = Array.isArray(coursesDataRaw)
          ? coursesDataRaw
          : Array.isArray(coursesDataRaw?.courses)
          ? coursesDataRaw.courses
          : [];
        if (!isMounted) return;
        setCourseOptions(coursesArray);
      } catch (error) {
        if (isMounted) {
          console.error("Failed to fetch courses:", error);
          setCourseOptions([]);
        }
      } finally {
        if (isMounted) {
          setCourseLoading(false);
        }
      }
    };
    fetchCourses();
    return () => { isMounted = false; };
  }, [examFormik.values.departmentId]);

  // Fetch classes for Create Exam form using /class/get-all-classes (filter by courseId if API returns all)
  useEffect(() => {
    let isMounted = true;
    const fetchClasses = async () => {
      const courseId = examFormik.values.courseId;
      if (!courseId) {
        setClassOptions((prev) => (prev.length === 0 ? prev : []));
        setClassLoading(false);
        return;
      }
      setClassLoading(true);
      try {
        const response = await courseService.getClasses({ courseId, limit: 1000 });
        const raw = response?.data?.data || response?.data || [];
        const list = Array.isArray(raw) ? raw : [];
        const filtered =
          courseId && list.some((c) => (c.courseId ?? c.course_id) != null)
            ? list.filter((c) => String(c.courseId ?? c.course_id) === String(courseId))
            : list;
        console.log(filtered)
        if (!isMounted) return;
        setClassOptions(filtered);
      } catch (error) {
        if (isMounted) {
          console.error("Failed to fetch classes:", error);
          setClassOptions([]);
        }
      } finally {
        if (isMounted) {
          setClassLoading(false);
        }
      }
    };
    fetchClasses();
    return () => { isMounted = false; };
  }, [examFormik.values.courseId]);

  // Fetch subjects based on class
  useEffect(() => {
    let isMounted = true;
    const fetchSubjects = async () => {
      if (!examFormik.values.classId) {
        setSubjectOptions((prev) => (prev.length === 0 ? prev : []));
        setSubjectLoading(false);
        return;
      }
      setSubjectLoading(true);
      try {
        const params = {
          id: examFormik.values.classId,
        };
        if (examFormik.values.courseId) {
          params.courseId = examFormik.values.courseId;
        }
        if (examFormik.values.departmentId) {
          params.departmentId = examFormik.values.departmentId;
        }
        const response = await assignmentService.getAssignmentSubjectNames(params);
        const subjectsDataRaw = response?.data?.data || response?.data || [];
        const subjectsArray = Array.isArray(subjectsDataRaw)
          ? subjectsDataRaw
          : Array.isArray(subjectsDataRaw?.subjects)
          ? subjectsDataRaw.subjects
          : [];
        if (!isMounted) return;
        setSubjectOptions(subjectsArray);
      } catch (error) {
        if (isMounted) {
          console.error("Failed to fetch subjects:", error);
          setSubjectOptions([]);
        }
      } finally {
        if (isMounted) {
          setSubjectLoading(false);
        }
      }
    };
    fetchSubjects();
    return () => { isMounted = false; };
  }, [examFormik.values.classId, examFormik.values.courseId, examFormik.values.departmentId]);

  // Fetch students for selected class (and subject when chosen — API validates class ↔ subject)
  useEffect(() => {
    let isMounted = true;
    const fetchStudents = async () => {
      if (!examFormik.values.classId) {
        setStudentOptions([]);
        setStudentLoading(false);
        return;
      }
      setStudentLoading(true);
      try {
        const response = await instituteService.getAllInstituteStudents({
          page: 1,
          limit: 1000,
          sort: "firstName",
          order: "ASC",
          classId: examFormik.values.classId,
          ...(examFormik.values.subjectId
            ? { subjectId: examFormik.values.subjectId }
            : {}),
          _t: Date.now(),
        });
        const payload = response?.data;
        const rawStudents = Array.isArray(payload?.data)
          ? payload.data
          : payload?.data?.data?.students ||
            payload?.data?.students ||
            payload?.data?.data?.rows ||
            payload?.data?.rows ||
            payload?.data?.data ||
            [];
        const allStudents = Array.isArray(rawStudents) ? rawStudents : [];
        const userInstituteId = Number(user?.instituteId || 0);
        const selectedClassId = Number(examFormik.values.classId || 0);
        const normalizedStudents = allStudents
          .map((student) => {
            const userRow = student?.user || {};
            const rawId = student?.id ?? student?.studentId ?? null;
            const safeId = Number(rawId);
            const rawClassId =
              student?.classId ??
              student?.classID ??
              student?.Class?.id ??
              student?.class?.id ??
              null;
            return {
              ...student,
              id: Number.isNaN(safeId) ? rawId : safeId,
              instituteId: Number(student?.instituteId ?? student?.Institute?.id ?? 0) || null,
              classId: rawClassId != null ? Number(rawClassId) : null,
              firstName: student?.firstName || userRow?.firstName || "",
              lastName: student?.lastName || userRow?.lastName || "",
              fullName:
                student?.fullName ||
                userRow?.fullName ||
                [student?.firstName || userRow?.firstName, student?.lastName || userRow?.lastName]
                  .filter(Boolean)
                  .join(" ")
                  .trim(),
              studentLoginID: student?.studentLoginID || userRow?.studentLoginID || "",
            };
          })
          .filter((student) => {
            if (!student?.id) return false;
            if (userInstituteId > 0 && student.instituteId != null && student.instituteId !== userInstituteId) {
              return false;
            }
            if (selectedClassId > 0 && student.classId != null && student.classId !== selectedClassId) {
              return false;
            }
            return true;
          });
        if (!isMounted) return;
        setStudentOptions(normalizedStudents);
      } catch (error) {
        if (isMounted) {
          console.error("Failed to fetch students:", error);
          setStudentOptions([]);
        }
      } finally {
        if (isMounted) {
          setStudentLoading(false);
        }
      }
    };
    fetchStudents();
    return () => {
      isMounted = false;
    };
  }, [examFormik.values.classId, examFormik.values.subjectId]);

  // Fetch courses based on department filter
  useEffect(() => {
    let isMounted = true;
    const fetchCoursesForFilter = async () => {
      if (!departmentIdFilter) {
        if (isMounted) {
          setCourseOptions((prev) => (prev.length === 0 ? prev : []));
          setCourseLoading(false);
        }
        return;
      }
      setCourseLoading(true);
      try {
        const params = {
          id: departmentIdFilter,
          departmentId: departmentIdFilter,
        };
        const response = await assignmentService.getAssignmentCourseNames(params);
        const coursesDataRaw = response?.data?.data || response?.data || [];
        const coursesArray = Array.isArray(coursesDataRaw)
          ? coursesDataRaw
          : Array.isArray(coursesDataRaw?.courses)
          ? coursesDataRaw.courses
          : [];
        if (!isMounted) return;
        setCourseOptions(coursesArray);
      } catch (error) {
        if (isMounted) {
          console.error("Failed to fetch courses for filter:", error);
          setCourseOptions([]);
        }
      } finally {
        if (isMounted) {
          setCourseLoading(false);
        }
      }
    };
    fetchCoursesForFilter();
    return () => { isMounted = false; };
  }, [departmentIdFilter]);

  // Fetch classes based on course filter
  useEffect(() => {
    let isMounted = true;
    const fetchClassesForFilter = async () => {
      if (!courseIdFilter) {
        if (isMounted) {
          setClassOptions((prev) => (prev.length === 0 ? prev : []));
          setClassLoading(false);
        }
        return;
      }
      setClassLoading(true);
      try {
        const response = await courseService.getCourseClasses({
          id: courseIdFilter,
        });
        const classesData = response?.data?.data || response?.data || [];
        if (!isMounted) return;
        setClassOptions(Array.isArray(classesData) ? classesData : []);
      } catch (error) {
        if (isMounted) {
          console.error("Failed to fetch classes for filter:", error);
          setClassOptions([]);
        }
      } finally {
        if (isMounted) {
          setClassLoading(false);
        }
      }
    };
    fetchClassesForFilter();
    return () => { isMounted = false; };
  }, [courseIdFilter]);

  // Fetch subjects based on class filter
  useEffect(() => {
    let isMounted = true;
    const fetchSubjectsForFilter = async () => {
      if (!classIdFilter) {
        if (isMounted) {
          setSubjectOptions((prev) => (prev.length === 0 ? prev : []));
          setSubjectLoading(false);
        }
        return;
      }
      setSubjectLoading(true);
      try {
        const params = {
          id: classIdFilter,
        };
        if (courseIdFilter) {
          params.courseId = courseIdFilter;
        }
        if (departmentIdFilter) {
          params.departmentId = departmentIdFilter;
        }
        const response = await assignmentService.getAssignmentSubjectNames(params);
        const subjectsDataRaw = response?.data?.data || response?.data || [];
        const subjectsArray = Array.isArray(subjectsDataRaw)
          ? subjectsDataRaw
          : Array.isArray(subjectsDataRaw?.subjects)
          ? subjectsDataRaw.subjects
          : [];
        if (!isMounted) return;
        setSubjectOptions(subjectsArray);
      } catch (error) {
        if (isMounted) {
          console.error("Failed to fetch subjects for filter:", error);
          setSubjectOptions([]);
        }
      } finally {
        if (isMounted) {
          setSubjectLoading(false);
        }
      }
    };
    fetchSubjectsForFilter();
    return () => { isMounted = false; };
  }, [classIdFilter, courseIdFilter, departmentIdFilter]);

  // Handle cascading filter changes for list
  const handleDepartmentFilterChange = (value) => {
    setDepartmentIdFilter(value);
    setCourseIdFilter('');
    setClassIdFilter('');
    setSubjectIdFilter('');
    setPaginationModel((p) => ({ ...p, page: 0 }));
  };

  const handleCourseFilterChange = (value) => {
    setCourseIdFilter(value);
    setClassIdFilter('');
    setSubjectIdFilter('');
    setPaginationModel((p) => ({ ...p, page: 0 }));
  };

  const handleClassFilterChange = (value) => {
    setClassIdFilter(value);
    setSubjectIdFilter('');
    setPaginationModel((p) => ({ ...p, page: 0 }));
  };

  const handleSubjectFilterChange = (value) => {
    setSubjectIdFilter(value);
    setPaginationModel((p) => ({ ...p, page: 0 }));
  };

  // Derive banner display from filter dropdown options (same API data) so "available subjects and classes" show dynamically
  const bannerDisplayFromFilters = useMemo(() => {
    const classNames = courseIdFilter && Array.isArray(classOptions) && classOptions.length > 0
      ? classOptions.map((c) => c?.name ?? c?.className ?? c?.label).filter(Boolean)
      : [];
    const subjectNames = classIdFilter && Array.isArray(subjectOptions) && subjectOptions.length > 0
      ? subjectOptions.map((s) => s?.name ?? s?.subjectName ?? s?.label).filter(Boolean)
      : [];
    return { classNames: [...new Set(classNames)], subjectNames: [...new Set(subjectNames)] };
  }, [courseIdFilter, classIdFilter, classOptions, subjectOptions]);

  // API params: search, page, limit, sort, order, status, classId, subjectId
  const examQueryParams = useMemo(() => {
    const statusMap = { 0: "", 1: "marking", 2: "completed", 3: "graded" };
    const params = {
      search: (searchQuery || "").trim(),
      page: paginationModel.page + 1,
      limit: paginationModel.pageSize,
      sort: "",
      order: "DESC",
      status: statusMap[activeTab] ?? "",
      subjectId: subjectIdFilter || "",
      classId: classIdFilter || "",
      departmentId: departmentIdFilter || "",
      courseId: courseIdFilter || "",
    };
    return params;
  }, [searchQuery, paginationModel.page, paginationModel.pageSize, activeTab, classIdFilter, subjectIdFilter, departmentIdFilter, courseIdFilter]);

  useEffect(() => {
    let cancelled = false;
    setExamsLoading(true);
    setExamsError(null);
    api
      .get("/exam/all", { params: examQueryParams })
      .then((res) => {
        if (cancelled) return;
        // API returns { data: { exams: [...], pagination: {...} } } - get array from data.exams
        const raw = res?.data?.data?.exams ?? res?.data?.exams ?? res?.data?.data ?? res?.data?.list ?? res?.data ?? [];
        const list = Array.isArray(raw) ? raw : [];
        const sorted = sortExamsWithUpcomingFirst(list.map(normalizeExamRow));
        const pagination = res?.data?.data?.pagination ?? res?.data?.pagination ?? res?.data?.meta ?? res?.pagination ?? res?.meta ?? {};
        const total = pagination.total ?? pagination.totalItems ?? res?.data?.total ?? list.length;
        const totalExamsVal = typeof total === "number" ? total : list.length;
        setExams(sorted);
        setTotalExams(totalExamsVal);
        examListCache = { exams: sorted, totalExams: totalExamsVal };
      })
      .catch((err) => {
        if (!cancelled) {
          setExamsError(err?.response?.data?.message ?? err?.message ?? "Failed to load exams");
          setExams([]);
        }
      })
      .finally(() => {
        if (!cancelled) setExamsLoading(false);
      });
    return () => { cancelled = true; };
  }, [examQueryParams]);

  const [markDialogStudents, setMarkDialogStudents] = useState([]);
  const [markSearchQuery, setMarkSearchQuery] = useState("");
  const [markSortField, setMarkSortField] = useState("name");
  const [markSortDirection, setMarkSortDirection] = useState("asc");
  const [markDialogLoading, setMarkDialogLoading] = useState(false);
  const [markDialogError, setMarkDialogError] = useState(null);
  const [saveMarksLoading, setSaveMarksLoading] = useState(false);
  const [importMarksLoading, setImportMarksLoading] = useState(false);
  const [templateDownloadLoading, setTemplateDownloadLoading] = useState(false);
  const [studentMarks, setStudentMarks] = useState({});
  const [marksValidationErrors, setMarksValidationErrors] = useState({});

  const [examTypes, setExamTypes] = useState([]);
  const [examTypesLoading, setExamTypesLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    setExamTypesLoading(true);
    (async () => {
      try {
        const params = new URLSearchParams();
        if (user?.instituteId != null && user.instituteId !== "") params.set("instituteId", String(user.instituteId));
        const qs = params.toString();
        const res = await api.get(qs ? `/academic-info/exam-types/all?${qs}` : "/academic-info/exam-types/all");
        const list = res?.data?.data ?? res?.data ?? [];
        if (!Array.isArray(list) || cancelled) return;
        const next = list.map((x) => ({ id: x.key, label: x.label, color: x.color || "default" })).filter((x) => x.id && x.label);
        if (!cancelled) setExamTypes(next);
      } catch {
        if (!cancelled) setExamTypes([]);
      } finally {
        if (!cancelled) setExamTypesLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [user?.instituteId]);

  const getExamTypeColor = (type) => {
    const key = String(type || "").toLowerCase().replace(/\s/g, "");
    if (!key) return "default";
    return examTypes.find((t) => String(t.id || "").toLowerCase() === key)?.color ?? "default";
  };

  useEffect(() => {
    if (!createDialogOpen) return;
    if (examDialogMode !== "edit" || !selectedExam) return;
    examFormikRef.current.resetForm({ values: mapExamToFormValues(selectedExam) });
    examFormikRef.current.setStatus(null);
  }, [createDialogOpen, examDialogMode, selectedExam]);

  const getStatusColor = (status) => {
    switch (status) {
      case "upcoming":
        return "warning";
      case "ongoing":
        return "info";
      case "marking":
        return "secondary";
      case "completed":
      case "graded":
        return "success";
      case "cancelled":
        return "error";
      default:
        return "default";
    }
  };

  const columns = [
    {
      field: "name",
      headerName: "Exam",
      flex: 1,
      minWidth: 320,
      renderCell: (params) => (
        <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
          <Avatar
            sx={{
              bgcolor: `${getExamTypeColor(params.row.examType)}.main`,
              width: 40,
              height: 40,
            }}
          >
            <Quiz />
          </Avatar>
          <Box>
            <Typography variant="body2" fontWeight={500}>
              {params.row.name}
            </Typography>
            <Typography variant="caption" color="text.secondary">
              {params.row.subject} • {params.row.class}
            </Typography>
          </Box>
        </Box>
      ),
    },
    {
      field: "examType",
      headerName: "Type",
      width: 100,
      renderCell: (params) => {
        const display = params.row.examTypeDisplay || params.value || "";
        return (
        <Chip
          label={display.charAt(0).toUpperCase() + display.slice(1)}
          size="small"
          color={getExamTypeColor(display)}
        />
        );
      },
    },
    {
      field: "date",
      headerName: "Date & Time",
      width: 180,
      renderCell: (params) => (
        <Box>
          <Typography variant="body2">{params.row.date}</Typography>
          <Typography variant="caption" color="text.secondary">
            {params.row.time}
          </Typography>
        </Box>
      ),
    },
    {
      field: "totalMarks",
      headerName: "Marks",
      width: 100,
      renderCell: (params) => (
        <Typography variant="body2">
          {params.row.totalMarks} ({params.row.passingMarks} pass)
        </Typography>
      ),
    },
    {
      field: "progress",
      headerName: "Marking Progress",
      width: 150,
      renderCell: (params) => {
        const rawTotal = Math.max(0, Number(params.row.studentsCount) || 0);
        const marked = Math.max(0, Number(params.row.markedCount) || 0);
        const total = rawTotal > 0 ? rawTotal : marked;
        const pct = total > 0 ? Math.min(100, (marked / total) * 100) : 0;
        return (
        <Box sx={{ width: "100%" }}>
          <LinearProgress
            variant="determinate"
            value={pct}
            sx={{ height: 8, borderRadius: 4 }}
          />
          <Typography variant="caption">
            {marked}/{total}
          </Typography>
        </Box>
        );
      },
    },
    {
      field: "status",
      headerName: "Status",
      width: 110,
      renderCell: (params) => (
        <Chip
          label={params.value.charAt(0).toUpperCase() + params.value.slice(1)}
          size="small"
          color={getStatusColor(params.value)}
        />
      ),
    },
    {
      field: "statusUpdate",
      headerName: "Complete/Cancel",
      width: 180,
      minWidth: 160,
      renderCell: (params) => {
        const isUpcoming = (params.row.status || "").toLowerCase() === "upcoming";
        const updating = statusUpdateId === params.row.id;
        if (!isUpcoming) {
          return (
            <Typography variant="body2" color="text.secondary">
              -
            </Typography>
          );
        }
        return (
          <Box sx={{ display: "flex", gap: 0.5 }}>
            <IconButton
              size="small"
              color="success"
              title="Mark as Completed"
              disabled={updating}
              onClick={() => {
                setStatusConfirmPayload({
                  examId: params.row.id,
                  examName: params.row.name || "this exam",
                  action: "completed",
                });
                setStatusConfirmOpen(true);
              }}
            >
              {updating ? <CircularProgress size={20} /> : <CheckCircle />}
            </IconButton>
            <IconButton
              size="small"
              color="error"
              title="Mark as Cancelled"
              disabled={updating}
              onClick={() => {
                setStatusConfirmPayload({
                  examId: params.row.id,
                  examName: params.row.name || "this exam",
                  action: "cancelled",
                });
                setStatusConfirmOpen(true);
              }}
            >
              <CancelIcon />
            </IconButton>
          </Box>
        );
      },
    },
    {
      field: "actions",
      headerName: "Actions",
      width: 180,
      renderCell: (params) => {
        const status = (params.row.status || "").toLowerCase();
        const disableEditDelete = status === "completed" || status === "cancelled";
        return (
          <Box>
            <IconButton
              size="small"
              onClick={() => {
                setSelectedExam(params.row);
                setViewDialogOpen(true);
              }}
            >
              <Visibility />
            </IconButton>
            <PermissionGate permission={HALL_TICKET_PERMISSIONS.CONFIGURE}>
              <IconButton
                size="small"
                color="primary"
                title="Hall Ticket"
                onClick={() =>
                  navigate(`${rolePath}/exam-management/${params.row.id}/hall-ticket`)
                }
              >
                <ConfirmationNumber />
              </IconButton>
            </PermissionGate>
            {(params.row.status === "completed" || params.row.status === "marking" || params.row.status === "graded") && (
              <PermissionGate permission="exam.mark_score">
                <IconButton
                  size="small"
                  color="primary"
                  onClick={() => {
                    setSelectedExam(params.row);
                    setMarkDialogOpen(true);
                  }}
                  title="Grade Students"
                >
                  <Grade />
                </IconButton>
              </PermissionGate>
            )}
            <PermissionGate permission="exam.update">
              <IconButton
                size="small"
                onClick={() => openEditExamDialog(params.row)}
                disabled={disableEditDelete}
              >
                <Edit />
              </IconButton>
            </PermissionGate>
            <PermissionGate permission="exam.delete">
              <IconButton
                size="small"
                color="error"
                disabled={disableEditDelete}
                onClick={() => openDeleteConfirm(params.row)}
              >
                <Delete />
              </IconButton>
            </PermissionGate>
          </Box>
        );
      },
    },
  ];

  const openCreateExamDialog = () => {
    setExamDialogMode("create");
    setEditingExamId(null);
    setSelectedExam(null);
    setCreateDialogOpen(true);
    examFormikRef.current.resetForm({ values: { ...defaultExamFormValuesRef.current } });
    examFormikRef.current.setStatus(null);
  };
  const openEditExamDialog = (row) => {
    setSelectedExam(row);
    setExamDialogMode("edit");
    setEditingExamId(row.id);
    setCreateDialogOpen(true);
  };
  const closeExamDialog = () => {
    setCreateDialogOpen(false);
    setEditingExamId(null);
    setExamDialogMode("create");
    setSelectedExam((prev) => (prev && viewDialogOpen ? prev : null));
    examFormikRef.current.resetForm({ values: { ...defaultExamFormValuesRef.current } });
    examFormikRef.current.setStatus(null);
  };

  const handleSnackbarClose = (event, reason) => {
    if (reason === "clickaway") return;
    setSnackbar((prev) => ({ ...prev, open: false }));
  };

  const handleStatusConfirm = async () => {
    const { examId, action } = statusConfirmPayload;
    if (!examId || !action) return;
    setStatusConfirmOpen(false);
    setStatusUpdateId(examId);
    try {
      await api.put("/exam/status-updated", { status: action }, { params: { id: examId } });
      setSnackbar({
        open: true,
        message: action === "completed" ? "Exam marked as completed" : "Exam marked as cancelled",
        severity: "success",
      });
      const res = await api.get("/exam/all", { params: examQueryParams });
      const raw = res?.data?.data?.exams ?? res?.data?.exams ?? [];
      const list = Array.isArray(raw) ? raw : [];
      setExams(sortExamsWithUpcomingFirst(list.map(normalizeExamRow)));
      const pagination = res?.data?.data?.pagination ?? res?.data?.pagination ?? {};
      setTotalExams(pagination.totalItems ?? pagination.total ?? list.length);
    } catch (err) {
      setSnackbar({
        open: true,
        message: err?.response?.data?.message ?? err?.message ?? "Failed to update status",
        severity: "error",
      });
    } finally {
      setStatusUpdateId(null);
      setStatusConfirmPayload({ examId: null, examName: "", action: null });
    }
  };

  const closeStatusConfirm = () => {
    setStatusConfirmOpen(false);
    setStatusConfirmPayload({ examId: null, examName: "", action: null });
  };

  const openDeleteConfirm = (row) => {
    setExamToDelete({ id: row.id, name: row.name || "this exam" });
    setDeleteConfirmOpen(true);
  };
  const closeDeleteConfirm = () => {
    setDeleteConfirmOpen(false);
    setExamToDelete({ id: null, name: "" });
  };
  const handleDeleteConfirm = async () => {
    if (!examToDelete.id) return;
    setDeleteLoading(true);
    try {
      await api.delete("/exam/delete", { params: { id: examToDelete.id } });
      setSnackbar({ open: true, message: "Exam deleted successfully", severity: "success" });
      closeDeleteConfirm();
      const res = await api.get("/exam/all", { params: examQueryParams });
      const raw = res?.data?.data?.exams ?? res?.data?.exams ?? [];
      const list = Array.isArray(raw) ? raw : [];
      setExams(sortExamsWithUpcomingFirst(list.map(normalizeExamRow)));
      const pagination = res?.data?.data?.pagination ?? res?.data?.pagination ?? {};
      setTotalExams(pagination.totalItems ?? pagination.total ?? list.length);
    } catch (err) {
      setSnackbar({
        open: true,
        message: err?.response?.data?.message ?? err?.message ?? "Failed to delete exam",
        severity: "error",
      });
    } finally {
      setDeleteLoading(false);
    }
  };

  const handleExportReport = async () => {
    try {
      const params = { ...examQueryParams, page: 1, limit: 100000 };
      const res = await api.get("/exam/export", { params, responseType: "blob" });
      const blob = new Blob([res.data], { type: "text/csv" });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `exam-report-${Date.now()}.csv`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
    } catch (err) {
      setSnackbar({ open: true, message: err?.response?.data?.message ?? err?.message ?? "Export failed", severity: "error" });
    }
  };

  const maxMarks = selectedExam?.totalMarks ?? 100;

  const getMarksNumeric = (student) => {
    const raw = studentMarks[student.id] ?? student.marks ?? student.marksObtained;
    if (raw === "" || raw === null || raw === undefined) return null;
    const n = Number.parseInt(String(raw), 10);
    return Number.isNaN(n) ? null : n;
  };

  const getStudentGradeStatus = (student) => {
    const marks = getMarksNumeric(student);
    if (marks === null) return "unmarked";
    const passMark = selectedExam?.passingMarks ?? 35;
    return marks >= passMark ? "pass" : "fail";
  };

  const displayedMarkStudents = useMemo(() => {
    const q = markSearchQuery.trim().toLowerCase();
    let list = [...markDialogStudents];
    if (q) {
      list = list.filter((s) => {
        const name = String(s.name || s.fullName || "").toLowerCase();
        const roll = String(s.rollNumber ?? s.rollNo ?? "").toLowerCase();
        const sid = String(s.studentId ?? s.studentLoginID ?? s.id ?? "").toLowerCase();
        return name.includes(q) || roll.includes(q) || sid.includes(q);
      });
    }
    const dir = markSortDirection === "desc" ? -1 : 1;
    list.sort((a, b) => {
      if (markSortField === "roll") {
        const ar = String(a.rollNumber ?? a.rollNo ?? "");
        const br = String(b.rollNumber ?? b.rollNo ?? "");
        return ar.localeCompare(br, undefined, { numeric: true }) * dir;
      }
      if (markSortField === "studentId") {
        const as = String(a.studentId ?? a.studentLoginID ?? a.id ?? "");
        const bs = String(b.studentId ?? b.studentLoginID ?? b.id ?? "");
        return as.localeCompare(bs, undefined, { numeric: true }) * dir;
      }
      if (markSortField === "marks") {
        const am = getMarksNumeric(a);
        const bm = getMarksNumeric(b);
        if (am === null && bm === null) return 0;
        if (am === null) return 1;
        if (bm === null) return -1;
        return (am - bm) * dir;
      }
      if (markSortField === "status") {
        return getStudentGradeStatus(a).localeCompare(getStudentGradeStatus(b)) * dir;
      }
      const an = String(a.name || a.fullName || "").toLowerCase();
      const bn = String(b.name || b.fullName || "").toLowerCase();
      return an.localeCompare(bn) * dir;
    });
    return list;
  }, [
    markDialogStudents,
    markSearchQuery,
    markSortField,
    markSortDirection,
    studentMarks,
    selectedExam?.passingMarks,
  ]);

  const validateMarks = (value, studentId) => {
    if (value === "" || value === null || value === undefined) return null;
    const num = Number.parseInt(value, 10);
    if (Number.isNaN(num)) return "Must be a number";
    if (num < 0) return "Cannot be negative";
    if (num > maxMarks) return `Must be 0–${maxMarks}`;
    return null;
  };

  const handleMarkChange = (studentId, marks) => {
    const val = marks === "" || marks === null || marks === undefined ? null : marks;
    setStudentMarks((prev) => ({ ...prev, [studentId]: val }));
    const err = validateMarks(marks, studentId);
    setMarksValidationErrors((prev) => {
      const next = { ...prev };
      if (err) next[studentId] = err;
      else delete next[studentId];
      return next;
    });
  };

  // Fetch students when Mark Scores dialog opens
  useEffect(() => {
    if (!markDialogOpen || !selectedExam?.id) {
      setMarkDialogStudents([]);
      setMarkSearchQuery("");
      setMarkSortField("name");
      setMarkSortDirection("asc");
      setStudentMarks({});
      setMarkDialogError(null);
      setMarksValidationErrors({});
      return;
    }
    let cancelled = false;
    setMarkDialogLoading(true);
    setMarkDialogError(null);
    examManagementService
      .getExamStudents(selectedExam.id, {
        classId: selectedExam?.classId,
        subjectId: selectedExam?.subjectId,
      })
      .then((res) => {
        if (cancelled) return;
        const data = res?.data?.data ?? res?.data ?? {};
        const students = data.students ?? [];
        setMarkDialogStudents(students);
        const initialMarks = {};
        students.forEach((s) => {
          const m = s.marks ?? s.marksObtained;
          if (m !== null && m !== undefined) initialMarks[s.id] = m;
        });
        setStudentMarks(initialMarks);
        setMarksValidationErrors({});
      })
      .catch((err) => {
        if (!cancelled) {
          setMarkDialogError(err?.response?.data?.message ?? err?.message ?? "Failed to load students");
          setMarkDialogStudents([]);
        }
      })
      .finally(() => {
        if (!cancelled) setMarkDialogLoading(false);
      });
    return () => { cancelled = true; };
  }, [markDialogOpen, selectedExam?.id]);

  const handleSubmitMarks = async () => {
    if (!selectedExam?.id) return;
    const errors = {};
    markDialogStudents.forEach((s) => {
      const m = studentMarks[s.id] ?? s.marks ?? s.marksObtained;
      const err = validateMarks(m === null || m === undefined ? "" : String(m), s.id);
      if (err) errors[s.id] = err;
    });
    if (Object.keys(errors).length > 0) {
      setMarksValidationErrors(errors);
      setSnackbar({ open: true, message: "Please fix invalid marks before saving", severity: "error" });
      return;
    }
    setSaveMarksLoading(true);
    try {
      const scores = markDialogStudents.map((s) => {
        const raw = studentMarks[s.id] ?? s.marks ?? s.marksObtained;
        const parsed = raw === "" || raw === null || raw === undefined ? null : Number.parseInt(String(raw), 10);
        const marks = parsed !== null && !Number.isNaN(parsed) ? parsed : null;
        return { studentId: s.id, id: s.id, marks };
      });
      await examManagementService.markStudentScores(selectedExam.id, scores);
      setSnackbar({ open: true, message: "Marks saved successfully", severity: "success" });
      setMarkDialogOpen(false);
      const res = await api.get("/exam/all", { params: examQueryParams });
      const raw = res?.data?.data?.exams ?? res?.data?.exams ?? [];
      const list = Array.isArray(raw) ? raw : [];
      setExams(sortExamsWithUpcomingFirst(list.map(normalizeExamRow)));
      const pagination = res?.data?.data?.pagination ?? res?.data?.pagination ?? {};
      setTotalExams(pagination.totalItems ?? pagination.total ?? list.length);
    } catch (err) {
      setSnackbar({
        open: true,
        message: err?.response?.data?.message ?? err?.message ?? "Failed to save marks",
        severity: "error",
      });
    } finally {
      setSaveMarksLoading(false);
    }
  };

  const handleDownloadExamReport = async () => {
    if (!selectedExam?.id) return;
    try {
      const res = await examManagementService.getExamStudents(selectedExam.id, {
        classId: selectedExam?.classId,
        subjectId: selectedExam?.subjectId,
      });
      const data = res?.data?.data ?? res?.data ?? {};
      const students = data.students ?? [];
      const passingMarks = selectedExam.passingMarks ?? 35;
      const headers = ["Student ID", "Roll Number", "Student Name", "Marks Obtained", "Status"];
      const rows = [headers.join(",")];
      students.forEach((s) => {
        const m = s.marks ?? s.marksObtained;
        const status = m === null || m === undefined ? "Pending" : m >= passingMarks ? "Pass" : "Fail";
        const sid = String(s.studentId || "").replace(/"/g, '""');
        const rollNo = String(s.rollNumber ?? s.rollNo ?? "").replace(/"/g, '""');
        const name = (s.name || "").replace(/"/g, '""');
        rows.push(`"${sid}","${rollNo}","${name}",${m ?? ""},"${status}"`);
      });
      const csv = rows.join("\n");
      const blob = new Blob([csv], { type: "text/csv" });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `exam-${selectedExam.id}-${(selectedExam.name || "report").replace(/\s+/g, "-")}-${Date.now()}.csv`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
      setSnackbar({ open: true, message: "Report downloaded", severity: "success" });
    } catch (err) {
      setSnackbar({
        open: true,
        message: err?.response?.data?.message ?? err?.message ?? "Failed to download report",
        severity: "error",
      });
    }
  };

  const handleDownloadTemplate = async () => {
    if (!selectedExam?.id || templateDownloadLoading) return;
    setTemplateDownloadLoading(true);
    try {
      const res = await examManagementService.downloadMarksTemplate(selectedExam.id);
      const blob = new Blob([res.data], { type: "text/csv" });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `exam-${selectedExam.id}-marks-template.csv`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
      setSnackbar({ open: true, message: "Template downloaded", severity: "success" });
    } catch (err) {
      setSnackbar({
        open: true,
        message: err?.response?.data?.message ?? err?.message ?? "Failed to download template",
        severity: "error",
      });
    } finally {
      setTemplateDownloadLoading(false);
    }
  };

  const handleImportMarks = (e) => {
    const file = e?.target?.files?.[0];
    if (!file || !selectedExam?.id) return;

    const ext = (file.name || "").toLowerCase().split(".").pop();
    if (ext !== "csv") {
      setMarkDialogError("Invalid file format. Please upload a CSV file. Download the template for the correct format.");
      if (importFileInputRef.current) importFileInputRef.current.value = "";
      return;
    }

    setMarkDialogError(null);
    setImportMarksLoading(true);
    examManagementService
      .bulkUploadScores(selectedExam.id, file)
      .then((res) => {
        const updated = res?.data?.data?.updated ?? res?.data?.updated ?? 0;
        setSnackbar({
          open: true,
          message: `Successfully imported ${updated} score(s). Table updated.`,
          severity: "success",
        });
        return Promise.all([
          examManagementService.getExamStudents(selectedExam.id, {
            classId: selectedExam?.classId,
            subjectId: selectedExam?.subjectId,
          }),
          api.get("/exam/all", { params: examQueryParams }),
        ]);
      })
      .then(([studentsRes, examsRes]) => {
        const data = studentsRes?.data?.data ?? studentsRes?.data ?? {};
        const students = data.students ?? [];
        setMarkDialogStudents(students);
        const initialMarks = {};
        students.forEach((s) => {
          const m = s.marks ?? s.marksObtained;
          if (m !== null && m !== undefined) initialMarks[s.id] = m;
        });
        setStudentMarks(initialMarks);
        setMarksValidationErrors({});
        const raw = examsRes?.data?.data?.exams ?? examsRes?.data?.exams ?? [];
        const list = Array.isArray(raw) ? raw : [];
        setExams(sortExamsWithUpcomingFirst(list.map(normalizeExamRow)));
        const pagination = examsRes?.data?.data?.pagination ?? examsRes?.data?.pagination ?? {};
        setTotalExams(pagination.totalItems ?? pagination.total ?? list.length);
      })
      .catch((err) => {
        const res = err?.response?.data;
        const msg = res?.message ?? err?.message ?? "Failed to import marks";
        const errList = res?.errors;
        setMarkDialogError(errList && Array.isArray(errList) && errList.length > 0 ? { message: msg, errors: errList } : msg);
        setSnackbar({ open: true, message: msg, severity: "error" });
      })
      .finally(() => {
        setImportMarksLoading(false);
      });

    if (importFileInputRef.current) importFileInputRef.current.value = "";
  };

  return (
    <Box>
      <Box sx={{ display: "flex", justifyContent: "space-between", mb: 3 }}>
        <Box>
          <Typography variant="h5" fontWeight={600}>
            Exam Management
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Create exams and mark student scores for your assigned subjects
          </Typography>
        </Box>
        <Box sx={{ display: "flex", gap: 1 }}>
          <Button variant="outlined" startIcon={<Download />} onClick={handleExportReport} disabled={exams.length === 0}>
            Export Report
          </Button>
          <PermissionGate permission="exam.create">
            <Button
              variant="contained"
              startIcon={<Add />}
              onClick={openCreateExamDialog}
            >
              Create Exam
            </Button>
          </PermissionGate>
        </Box>
      </Box>

      {/* Teacher Assignment Info – show available subjects and classes (from API via filters or initial load) */}
      <Alert severity="info" sx={{ mb: 3 }}>
        <Typography variant="body2">
          {bannerAssignments.loading &&
          bannerDisplayFromFilters.subjectNames.length === 0 &&
          bannerDisplayFromFilters.classNames.length === 0 ? (
            "Loading your assigned subjects and classes…"
          ) : bannerDisplayFromFilters.subjectNames.length > 0 ||
            bannerDisplayFromFilters.classNames.length > 0 ? (
            <>
              You can create exams for:{" "}
              <strong>
                {bannerDisplayFromFilters.subjectNames.length > 0
                  ? bannerDisplayFromFilters.subjectNames.join(", ")
                  : "—"}
              </strong>{" "}
              in classes{" "}
              <strong>
                {bannerDisplayFromFilters.classNames.length > 0
                  ? bannerDisplayFromFilters.classNames.join(", ")
                  : "—"}
              </strong>
            </>
          ) : bannerAssignments.subjectNames.length > 0 || bannerAssignments.classNames.length > 0 ? (
            <>
              You can create exams for:{" "}
              <strong>
                {bannerAssignments.subjectNames.length > 0
                  ? bannerAssignments.subjectNames.join(", ")
                  : "—"}
              </strong>{" "}
              in classes{" "}
              <strong>
                {bannerAssignments.classNames.length > 0
                  ? bannerAssignments.classNames.join(", ")
                  : "—"}
              </strong>
            </>
          ) : (
            <>
              You can create exams for your assigned subjects and classes.{" "}
              <strong>Select Department, then Course, then Class</strong> in the filters below to see which subjects and classes are available.
            </>
          )}
        </Typography>
      </Alert>

      {/* Stats */}
      <Grid container spacing={3} sx={{ mb: 3 }}>
        {[
          {
            label: "Total Exams",
            value: totalExams,
            color: "primary.main",
            icon: <Quiz />,
          },
          {
            label: "Upcoming",
            value: exams.filter((e) => e.status === "upcoming").length,
            color: "warning.main",
            icon: <School />,
          },
          {
            label: "Pending Marking",
            value: exams.filter((e) => e.status === "marking").length,
            color: "secondary.main",
            icon: <Grade />,
          },
          {
            label: "Completed",
            value: exams.filter((e) => e.status === "completed").length,
            color: "success.main",
            icon: <Assessment />,
          },
        ].map((stat, i) => (
          <Grid item xs={6} sm={3} key={i}>
            <Card>
              <CardContent
                sx={{ display: "flex", alignItems: "center", gap: 2, py: 2 }}
              >
                <Avatar sx={{ bgcolor: stat.color }}>{stat.icon}</Avatar>
                <Box>
                  <Typography variant="h5" fontWeight={700}>
                    {stat.value}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    {stat.label}
                  </Typography>
                </Box>
              </CardContent>
            </Card>
          </Grid>
        ))}
      </Grid>

      {/* Tabs */}
      <Card sx={{ mb: 3 }}>
        <Tabs
          value={activeTab}
          onChange={(e, v) => {
            setActiveTab(v);
            setPaginationModel((p) => ({ ...p, page: 0 }));
          }}
        >
          <Tab label="All Exams" icon={<Quiz />} iconPosition="start" />
          <Tab label="Pending Marking" icon={<Grade />} iconPosition="start" />
          <Tab label="Completed" icon={<Assessment />} iconPosition="start" />
          <Tab label="Graded" icon={<CheckCircle />} iconPosition="start" />
        </Tabs>
      </Card>

      {/* Content */}
      <Card sx={{ overflow: "auto" }}>
        <CardContent sx={{ px: 2, py: 2 }}>
          <Box sx={{ mb: 2, display: "flex", gap: 2 }}>
            <TextField
              placeholder="Search exams..."
              size="small"
              value={searchQuery}
              onChange={(e) => {
                setSearchQuery(e.target.value);
                setPaginationModel((p) => ({ ...p, page: 0 }));
              }}
              onKeyDown={(e) => {
                if (e.key === "Enter") setPaginationModel((p) => ({ ...p, page: 0 }));
              }}
              sx={{ width: 300 }}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <Search />
                  </InputAdornment>
                ),
              }}
            />
            <FormControl size="small" sx={{ minWidth: 150 }}>
              <InputLabel>Department</InputLabel>
              <Select
                label="Department"
                value={departmentIdFilter}
                onChange={(e) => handleDepartmentFilterChange(e.target.value)}
                disabled={departmentLoading}
              >
                <MenuItem value="">All Departments</MenuItem>
                {departmentOptions.map((d) => (
                  <MenuItem key={d.id ?? d.departmentId ?? d.name} value={String(d.id ?? d.departmentId ?? d.value ?? "")}>
                    {d.name ?? d.departmentName ?? d.label}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            <FormControl size="small" sx={{ minWidth: 150 }}>
              <InputLabel>Course</InputLabel>
              <Select
                label="Course"
                value={courseIdFilter}
                onChange={(e) => handleCourseFilterChange(e.target.value)}
                disabled={courseLoading || !departmentIdFilter}
              >
                <MenuItem value="">All Courses</MenuItem>
                {courseOptions.map((c) => (
                  <MenuItem key={c.id ?? c.courseId ?? c.name} value={String(c.id ?? c.courseId ?? c.value ?? "")}>
                    {c.name ?? c.courseName ?? c.label}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            <FormControl size="small" sx={{ minWidth: 150 }}>
              <InputLabel>Class</InputLabel>
              <Select
                label="Class"
                value={classIdFilter}
                onChange={(e) => handleClassFilterChange(e.target.value)}
                disabled={classLoading || !courseIdFilter}
              >
                <MenuItem value="">All Classes</MenuItem>
                {classOptions.map((c) => (
                  <MenuItem key={c.id ?? c.classId ?? c.name} value={String(c.id ?? c.classId ?? c.value ?? "")}>
                    {[c.name ?? c.className ?? c.label, c.section].filter(Boolean).join(" - ")}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            <FormControl size="small" sx={{ minWidth: 150 }}>
              <InputLabel>Subject</InputLabel>
              <Select
                label="Subject"
                value={subjectIdFilter}
                onChange={(e) => handleSubjectFilterChange(e.target.value)}
                disabled={subjectLoading || !classIdFilter}
              >
                <MenuItem value="">All Subjects</MenuItem>
                {subjectOptions.map((s) => (
                  <MenuItem key={s.id ?? s.subjectId ?? s.name} value={String(s.id ?? s.subjectId ?? s.value ?? "")}>
                    {s.name ?? s.subjectName ?? s.label}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Box>

          {examsError && (
            <Alert severity="error" sx={{ mb: 2 }} onClose={() => setExamsError(null)}>
              {examsError}
            </Alert>
          )}

          <Box sx={{ position: "relative", minHeight: 200 }}>
            <DataGrid
              rows={exams}
              columns={columns}
              rowCount={totalExams}
              paginationMode="server"
              paginationModel={paginationModel}
              onPaginationModelChange={setPaginationModel}
              loading={examsLoading}
              autoHeight
              disableRowSelectionOnClick
              pageSizeOptions={PAGINATION_PAGE_SIZE_OPTIONS}
              sx={{ border: "none" }}
            />
          </Box>
        </CardContent>
      </Card>

      {/* Create / Edit Exam Dialog */}
      <Dialog
        open={createDialogOpen}
        onClose={(e, reason) => {
          if (reason === "backdropClick" || reason === "escapeKeyDown") return;
          closeExamDialog();
        }}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>
          <Box sx={{ display: "flex", justifyContent: "space-between" }}>
            {examDialogMode === "edit" ? "Edit Exam" : "Create New Exam"}
            <IconButton onClick={closeExamDialog}>
              <Close />
            </IconButton>
          </Box>
        </DialogTitle>
        <Box component="form" onSubmit={examFormik.handleSubmit} noValidate>
          <DialogContent>
            <Grid container spacing={2} sx={{ pt: 0 }}>
              {examFormik.status && (
                <Grid item xs={12}>
                  <Alert severity="error" onClose={() => examFormik.setStatus(null)}>
                    {examFormik.status}
                  </Alert>
                </Grid>
              )}
              {examFormik.submitCount > 0 && Object.keys(examFormik.errors).length > 0 && (
                <Grid item xs={12}>
                  <Alert severity="error">
                    Please fill all required fields (including Exam Date, Start Time, and End Time) before creating the exam.
                  </Alert>
                </Grid>
              )}
              <Grid item xs={12}>
                <Alert severity="info" sx={{ pt: 1, mb: 1 }}>
                  <Typography variant="body2">
                    {bannerAssignments.loading ? (
                      "Loading your assigned subjects and classes…"
                    ) : bannerAssignments.subjectNames.length > 0 || bannerAssignments.classNames.length > 0 ? (
                      <>
                        You can only create exams for:{" "}
                        <strong>
                          {bannerAssignments.subjectNames.length > 0
                            ? bannerAssignments.subjectNames.join(", ")
                            : "—"}
                        </strong>{" "}
                        in classes{" "}
                        <strong>
                          {bannerAssignments.classNames.length > 0
                            ? bannerAssignments.classNames.join(", ")
                            : "—"}
                        </strong>
                      </>
                    ) : (
                      <>
                        You can only create exams for your assigned subjects and classes.{" "}
                        Select Department, then Course, then Class below to see your options.
                      </>
                    )}
                  </Typography>
                </Alert>
              </Grid>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  name="name"
                  label="Exam Name *"
                  placeholder="e.g., Mid-Term Mathematics Exam"
                  value={examFormik.values.name}
                  onChange={handleExamNameChange}
                  onBlur={examFormik.handleBlur}
                  error={(examFormik.touched.name || examFormik.submitCount > 0) && Boolean(examFormik.errors.name)}
                  helperText={
                    (examFormik.touched.name || examFormik.submitCount > 0) && examFormik.errors.name
                      ? examFormik.errors.name
                      : `${(examFormik.values.name || "").length}/${EXAM_NAME_MAX} characters`
                  }
                  inputProps={{ maxLength: EXAM_NAME_MAX, minLength: EXAM_NAME_MIN }}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <FormControl
                  fullWidth
                  error={(examFormik.touched.departmentId || examFormik.submitCount > 0) && Boolean(examFormik.errors.departmentId)}
                >
                  <InputLabel>Department *</InputLabel>
                  <Select
                    name="departmentId"
                    label="Department"
                    value={examFormik.values.departmentId}
                    onChange={(e) => handleDepartmentChange(e.target.value)}
                    onBlur={examFormik.handleBlur}
                    disabled={departmentLoading}
                  >
                    <MenuItem value="">Select department</MenuItem>
                    {departmentOptions.map((dept) => (
                      <MenuItem key={dept.id ?? dept.departmentId ?? dept.name} value={dept.id ?? dept.departmentId ?? dept.value}>
                        {dept.name ?? dept.departmentName ?? dept.label}
                      </MenuItem>
                    ))}
                  </Select>
                  <FormHelperText>
                    {(examFormik.touched.departmentId || examFormik.submitCount > 0) && examFormik.errors.departmentId}
                  </FormHelperText>
                </FormControl>
              </Grid>
              <Grid item xs={12} sm={6}>
                <FormControl
                  fullWidth
                  error={(examFormik.touched.courseId || examFormik.submitCount > 0) && Boolean(examFormik.errors.courseId)}
                >
                  <InputLabel>Course *</InputLabel>
                  <Select
                    name="courseId"
                    label="Course"
                    value={examFormik.values.courseId}
                    onChange={(e) => {
                      if (!examFormik.values.departmentId) return;
                      if (["__loading__", "__no_options__"].includes(String(e.target.value))) return;
                      handleCourseChange(e.target.value);
                    }}
                    onBlur={examFormik.handleBlur}
                  >
                    <MenuItem value="">Select course</MenuItem>
                    {courseLoading && (
                      <MenuItem value="__loading__">
                        <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                          <CircularProgress size={16} />
                          Loading...
                        </Box>
                      </MenuItem>
                    )}
                    {examFormik.values.departmentId && !courseLoading && courseOptions.length === 0 && (
                      <MenuItem value="__no_options__">No options found</MenuItem>
                    )}
                    {(examFormik.values.departmentId ? courseOptions : []).map((course) => (
                      <MenuItem key={course.id ?? course.courseId ?? course.name} value={course.id ?? course.courseId ?? course.value}>
                        {course.name ?? course.courseName ?? course.label}
                      </MenuItem>
                    ))}
                  </Select>
                  <FormHelperText>
                    {(examFormik.touched.courseId || examFormik.submitCount > 0) && examFormik.errors.courseId}
                  </FormHelperText>
                </FormControl>
              </Grid>
              <Grid item xs={12} sm={6}>
                <FormControl
                  fullWidth
                  error={(examFormik.touched.classId || examFormik.submitCount > 0) && Boolean(examFormik.errors.classId)}
                >
                  <InputLabel>Class *</InputLabel>
                  <Select
                    name="classId"
                    label="Class"
                    value={examFormik.values.classId}
                    onChange={(e) => {
                      if (!examFormik.values.courseId) return;
                      if (["__loading__", "__no_options__"].includes(String(e.target.value))) return;
                      handleClassChange(e.target.value);
                    }}
                    onBlur={examFormik.handleBlur}
                  >
                    <MenuItem value="">Select class</MenuItem>
                    {classLoading && (
                      <MenuItem value="__loading__">
                        <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                          <CircularProgress size={16} />
                          Loading...
                        </Box>
                      </MenuItem>
                    )}
                    {examFormik.values.courseId && !classLoading && classOptions.length === 0 && (
                      <MenuItem value="__no_options__">No options found</MenuItem>
                    )}
                    {(examFormik.values.courseId ? classOptions : []).map((c) => {
                      const name = c.name ?? c.className ?? c.label ?? "";
                      const section = c.sectionName ?? c.section ?? "";
                      const label = section ? `${name} - ${section}` : name || "—";
                      return (
                        <MenuItem key={c.id ?? c.classId ?? `class-${c.id ?? c.classId}`} value={c.id ?? c.classId ?? c.value}>
                          {label}
                        </MenuItem>
                      );
                    })}
                  </Select>
                  <FormHelperText>
                    {(examFormik.touched.classId || examFormik.submitCount > 0) && examFormik.errors.classId}
                  </FormHelperText>
                </FormControl>
              </Grid>
              <Grid item xs={12} sm={6}>
                <FormControl
                  fullWidth
                  error={(examFormik.touched.subjectId || examFormik.submitCount > 0) && Boolean(examFormik.errors.subjectId)}
                >
                  <InputLabel>Subject *</InputLabel>
                  <Select
                    name="subjectId"
                    label="Subject"
                    value={examFormik.values.subjectId}
                    onChange={(e) => {
                      if (!examFormik.values.classId) return;
                      if (["__loading__", "__no_options__"].includes(String(e.target.value))) return;
                      handleSubjectChange(e.target.value);
                    }}
                    onBlur={examFormik.handleBlur}
                  >
                    <MenuItem value="">Select subject</MenuItem>
                    {subjectLoading && (
                      <MenuItem value="__loading__">
                        <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                          <CircularProgress size={16} />
                          Loading...
                        </Box>
                      </MenuItem>
                    )}
                    {examFormik.values.classId && !subjectLoading && subjectOptions.length === 0 && (
                      <MenuItem value="__no_options__">No options found</MenuItem>
                    )}
                    {(examFormik.values.classId ? subjectOptions : []).map((s) => (
                      <MenuItem key={s.id ?? s.subjectId ?? s.name} value={s.id ?? s.subjectId ?? s.value}>
                        {s.name ?? s.subjectName ?? s.label}
                      </MenuItem>
                    ))}
                  </Select>
                  <FormHelperText>
                    {(examFormik.touched.subjectId || examFormik.submitCount > 0) && examFormik.errors.subjectId}
                  </FormHelperText>
                </FormControl>
              </Grid>
              <Grid item xs={4}>
                <FormControl
                  fullWidth
                  error={(examFormik.touched.examType || examFormik.submitCount > 0) && Boolean(examFormik.errors.examType)}
                >
                  <InputLabel shrink>Exam Type *</InputLabel>
                  <Select
                    name="examType"
                    label="Exam Type"
                    value={examFormik.values.examType}
                    displayEmpty
                    disabled={examTypesLoading}
                    renderValue={(v) => (examTypesLoading ? "Loading…" : (!v ? "Select exam type" : (examTypes.find((x) => String(x.id).toLowerCase() === String(v).toLowerCase())?.label ?? v)))}
                    onChange={examFormik.handleChange}
                    onBlur={examFormik.handleBlur}
                  >
                    <MenuItem value="" disabled>Select exam type</MenuItem>
                    {examTypes.map((t) => (
                      <MenuItem key={t.id} value={t.id}>
                        {t.label}
                      </MenuItem>
                    ))}
                  </Select>
                  <FormHelperText>
                    {!examTypesLoading && (examFormik.touched.examType || examFormik.submitCount > 0) && examFormik.errors.examType}
                  </FormHelperText>
                </FormControl>
              </Grid>
              <Grid item xs={4}>
                <TextField
                  fullWidth
                  name="totalMarks"
                  label="Total Marks *"
                  type="number"
                  inputProps={{ min: EXAM_FORM_MIN_MARKS, max: EXAM_FORM_MAX_MARKS }}
                  value={examFormik.values.totalMarks === "" ? "" : examFormik.values.totalMarks}
                  onChange={examFormik.handleChange}
                  onBlur={examFormik.handleBlur}
                  error={(examFormik.touched.totalMarks || examFormik.submitCount > 0) && Boolean(examFormik.errors.totalMarks)}
                  helperText={(examFormik.touched.totalMarks || examFormik.submitCount > 0) && examFormik.errors.totalMarks}
                />
              </Grid>
              <Grid item xs={4}>
                <TextField
                  fullWidth
                  name="passingMarks"
                  label="Passing Marks *"
                  type="number"
                  inputProps={{ min: 0, max: examFormik.values.totalMarks || EXAM_FORM_MAX_MARKS }}
                  value={examFormik.values.passingMarks === "" ? "" : examFormik.values.passingMarks}
                  onChange={examFormik.handleChange}
                  onBlur={examFormik.handleBlur}
                  error={(examFormik.touched.passingMarks || examFormik.submitCount > 0) && Boolean(examFormik.errors.passingMarks)}
                  helperText={(examFormik.touched.passingMarks || examFormik.submitCount > 0) && examFormik.errors.passingMarks}
                />
              </Grid>
              <Grid item xs={4}>
                <DatePicker
                  label="Exam Date *"
                  disablePast
                  value={examFormik.values.examDate}
                  onChange={(date) => {
                    examFormik.setFieldValue("examDate", date);
                    examFormik.setFieldTouched("examDate", true);
                  }}
                  onBlur={() => examFormik.setFieldTouched("examDate", true)}
                  slotProps={{
                    textField: {
                      fullWidth: true,
                      error: Boolean(examFormik.errors.examDate),
                      helperText: examFormik.errors.examDate,
                    },
                  }}
                />
              </Grid>
              <Grid item xs={4}>
                <TimePicker
                  label="Start Time *"
                  value={examFormik.values.startTime}
                  timeSteps={{ minutes: 1 }}
                  onChange={(time) => {
                    examFormik.setFieldValue("startTime", time);
                    examFormik.setFieldTouched("startTime", true);
                  }}
                  onBlur={() => examFormik.setFieldTouched("startTime", true)}
                  slotProps={{
                    textField: {
                      fullWidth: true,
                      error: Boolean(examFormik.errors.startTime),
                      helperText: examFormik.errors.startTime,
                    },
                  }}
                />
              </Grid>
              <Grid item xs={4}>
                <TimePicker
                  label="End Time *"
                  value={examFormik.values.endTime}
                  timeSteps={{ minutes: 1 }}
                  onChange={(time) => {
                    examFormik.setFieldValue("endTime", time);
                    examFormik.setFieldTouched("endTime", true);
                  }}
                  onBlur={() => examFormik.setFieldTouched("endTime", true)}
                  slotProps={{
                    textField: {
                      fullWidth: true,
                      error: Boolean(examFormik.errors.endTime),
                      helperText: examFormik.errors.endTime,
                    },
                  }}
                />
              </Grid>
              <Grid item xs={12}>
                <Autocomplete
                  multiple
                  id="selectedStudents"
                  options={studentOptions}
                  disableCloseOnSelect
                  getOptionLabel={(option) => `${option.fullName || `${option.firstName || ''} ${option.lastName || ''}`.trim()} (${option.studentLoginID || ''})`}
                  value={studentOptions.filter(option => examFormik.values.selectedStudents.includes(option.id))}
                  onChange={(event, newValue) => {
                    examFormik.setFieldValue('selectedStudents', newValue.map(student => student.id));
                  }}
                  loading={studentLoading}
                  renderOption={(props, option, { selected }) => (
                    (() => {
                      const { key, ...restProps } = props;
                      return (
                    <li key={key} {...restProps}>
                      <Checkbox
                        style={{ marginRight: 8 }}
                        checked={selected}
                      />
                      {`${option.fullName || `${option.firstName || ''} ${option.lastName || ''}`.trim()} (${option.studentLoginID || ''})`}
                    </li>
                      );
                    })()
                  )}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      label="Select Students (Optional)"
                      placeholder="Choose students for this exam"
                      helperText="If no students selected, exam will be for entire class"
                    />
                  )}
                  disabled={studentLoading || !examFormik.values.classId}
                />
              </Grid>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  name="instruction"
                  label="Instructions"
                  multiline
                  rows={3}
                  placeholder="Enter exam instructions for students (min 3, max 100 characters)..."
                  value={examFormik.values.instruction}
                  onChange={examFormik.handleChange}
                  onBlur={examFormik.handleBlur}
                  error={examFormik.touched.instruction && Boolean(examFormik.errors.instruction)}
                  helperText={
                    examFormik.touched.instruction && examFormik.errors.instruction
                      ? examFormik.errors.instruction
                      : `${(examFormik.values.instruction || "").length}/${EXAM_INSTRUCTION_MAX} characters`
                  }
                  inputProps={{ maxLength: EXAM_INSTRUCTION_MAX, minLength: EXAM_INSTRUCTION_MIN }}
                />
              </Grid>
            </Grid>
          </DialogContent>
          <DialogActions>
            <Button type="button" onClick={closeExamDialog}>
              Cancel
            </Button>
            <Button
              type="submit"
              variant="contained"
              disabled={
                !(examFormik.values.examDate && dayjs(examFormik.values.examDate).isValid()) ||
                !(examFormik.values.startTime && dayjs(examFormik.values.startTime).isValid()) ||
                !(examFormik.values.endTime && dayjs(examFormik.values.endTime).isValid()) ||
                examFormik.isSubmitting ||
                departmentLoading ||
                courseLoading ||
                classLoading ||
                subjectLoading
              }
            >
              {examFormik.isSubmitting ? (
                <CircularProgress size={24} />
              ) : examDialogMode === "edit" ? (
                "Update Exam"
              ) : (
                "Create Exam"
              )}
            </Button>
          </DialogActions>
        </Box>
      </Dialog>

      {/* Mark Scores Dialog */}
      <Dialog
        open={markDialogOpen}
        onClose={(e, reason) => {
          if (reason === "backdropClick" || reason === "escapeKeyDown") return;
          setMarkDialogOpen(false);
        }}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>
          <Box sx={{ display: "flex", justifyContent: "space-between" }}>
            Mark Student Scores
            <IconButton onClick={() => setMarkDialogOpen(false)}>
              <Close />
            </IconButton>
          </Box>
        </DialogTitle>
        <DialogContent>
          {selectedExam && (
            <Box>
              <Card variant="outlined" sx={{ mb: 3 }}>
                <CardContent>
                  <Grid container spacing={2}>
                    <Grid item xs={6}>
                      <Typography variant="subtitle1" fontWeight={600}>
                        {selectedExam.name}
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        {selectedExam.subject} • {selectedExam.class}
                      </Typography>
                      <Chip
                        label={selectedExam.status?.toUpperCase()}
                        size="small"
                        color={getStatusColor(selectedExam.status)}
                        sx={{ mt: 1 }}
                      />
                    </Grid>
                    <Grid item xs={6} sx={{ textAlign: "right" }}>
                      <Typography variant="body2">
                        Total Marks: <strong>{selectedExam.totalMarks}</strong>
                      </Typography>
                      <Typography variant="body2">
                        Passing Marks:{" "}
                        <strong>{selectedExam.passingMarks}</strong>
                      </Typography>
                    </Grid>
                  </Grid>
                </CardContent>
              </Card>

              {selectedExam.status === "completed" && (
                <Alert severity="info" sx={{ mb: 2 }}>
                  <Typography variant="body2">
                    <strong>Exam Completed:</strong> You can now grade students. Grades will be automatically calculated based on the marks entered and your institute's grading scale.
                  </Typography>
                </Alert>
              )}

              {selectedExam.status === "upcoming" && (
                <Alert severity="warning" sx={{ mb: 2 }}>
                  <Typography variant="body2">
                    <strong>Cannot Grade:</strong> This exam is still upcoming. Please mark the exam as "Completed" before grading students.
                  </Typography>
                </Alert>
              )}

              <input
                type="file"
                ref={importFileInputRef}
                accept=".csv"
                style={{ display: "none" }}
                onChange={handleImportMarks}
              />
              <Box sx={{ display: "flex", gap: 2, mb: 2 }}>
                <Button
                  variant="outlined"
                  startIcon={importMarksLoading ? <CircularProgress size={20} /> : <Upload />}
                  onClick={() => importFileInputRef.current?.click()}
                  disabled={markDialogLoading || importMarksLoading}
                >
                  {importMarksLoading ? "Importing..." : "Import Marks"}
                </Button>
                <Button
                  variant="outlined"
                  startIcon={templateDownloadLoading ? <CircularProgress size={20} /> : <Download />}
                  onClick={handleDownloadTemplate}
                  disabled={markDialogLoading || importMarksLoading || templateDownloadLoading || !selectedExam?.id}
                >
                  {templateDownloadLoading ? "Downloading..." : "Download Template"}
                </Button>
              </Box>

              {markDialogError && (
                <Alert severity="error" sx={{ mb: 2 }} onClose={() => setMarkDialogError(null)}>
                  {typeof markDialogError === "object" && markDialogError?.errors ? (
                    <Box>
                      <Typography variant="body2" fontWeight={600} sx={{ mb: 1 }}>
                        {markDialogError.message}
                      </Typography>
                      <Box component="ul" sx={{ m: 0, pl: 2 }}>
                        {markDialogError.errors.map((e, idx) => (
                          <li key={idx}>
                            <Typography variant="body2" component="span">
                              {e}
                            </Typography>
                          </li>
                        ))}
                      </Box>
                      <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: "block" }}>
                        Tip: Only students enrolled in this exam can be imported. Use &quot;Download Template&quot; to get the correct format with enrolled students only.
                      </Typography>
                    </Box>
                  ) : (
                    String(markDialogError)
                  )}
                </Alert>
              )}
              {importMarksLoading && (
                <Alert severity="info" sx={{ mb: 2 }}>
                  Importing marks... Table will auto-fill when complete.
                </Alert>
              )}
              {!markDialogLoading && markDialogStudents.length > 0 && (
                <Grid container spacing={2} sx={{ mb: 2 }} alignItems="center">
                  <Grid item xs={12} md={5}>
                    <TextField
                      fullWidth
                      size="small"
                      placeholder="Search by name, roll number, or student ID"
                      value={markSearchQuery}
                      onChange={(e) => setMarkSearchQuery(e.target.value)}
                      InputProps={{
                        startAdornment: (
                          <InputAdornment position="start">
                            <Search fontSize="small" />
                          </InputAdornment>
                        ),
                      }}
                    />
                  </Grid>
                  <Grid item xs={8} md={4}>
                    <FormControl fullWidth size="small">
                      <InputLabel>Sort by</InputLabel>
                      <Select
                        label="Sort by"
                        value={markSortField}
                        onChange={(e) => setMarkSortField(e.target.value)}
                      >
                        <MenuItem value="name">Name</MenuItem>
                        <MenuItem value="roll">Roll Number</MenuItem>
                        <MenuItem value="studentId">Student ID</MenuItem>
                        <MenuItem value="marks">Marks</MenuItem>
                        <MenuItem value="status">Status</MenuItem>
                      </Select>
                    </FormControl>
                  </Grid>
                  <Grid item xs={4} md={3}>
                    <FormControl fullWidth size="small">
                      <InputLabel>Order</InputLabel>
                      <Select
                        label="Order"
                        value={markSortDirection}
                        onChange={(e) => setMarkSortDirection(e.target.value)}
                      >
                        <MenuItem value="asc">A-Z / Low-High</MenuItem>
                        <MenuItem value="desc">Z-A / High-Low</MenuItem>
                      </Select>
                    </FormControl>
                  </Grid>
                  <Grid item xs={12}>
                    <Typography variant="caption" color="text.secondary">
                      Showing {displayedMarkStudents.length} of {markDialogStudents.length} students
                    </Typography>
                  </Grid>
                </Grid>
              )}
              {markDialogLoading && (
                <Box sx={{ display: "flex", justifyContent: "center", py: 3 }}>
                  <CircularProgress />
                </Box>
              )}
              {!markDialogLoading && (
              <TableContainer component={Paper} sx={{ maxHeight: 420 }}>
                <Table>
                  <TableHead>
                    <TableRow>
                      <TableCell>Student ID</TableCell>
                      <TableCell>Roll Number</TableCell>
                      <TableCell>Student Name</TableCell>
                      <TableCell>Marks Obtained</TableCell>
                      <TableCell>Percentage</TableCell>
                      <TableCell>Grade</TableCell>
                      <TableCell>Status</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {displayedMarkStudents.map((student) => (
                      <TableRow key={student.id}>
                        <TableCell>{student.studentId || "—"}</TableCell>
                        <TableCell>{student.rollNumber ?? student.rollNo ?? "—"}</TableCell>
                        <TableCell>
                          <Box
                            sx={{
                              display: "flex",
                              alignItems: "center",
                              gap: 1,
                            }}
                          >
                            <Avatar sx={{ width: 32, height: 32 }}>
                              {student.name?.[0] || "?"}
                            </Avatar>
                            {student.name}
                          </Box>
                        </TableCell>
                        <TableCell>
                          <TextField
                            size="small"
                            type="number"
                            value={
                              studentMarks[student.id] ?? student.marks ?? student.marksObtained ?? ""
                            }
                            onChange={(e) =>
                              handleMarkChange(student.id, e.target.value)
                            }
                            error={Boolean(marksValidationErrors[student.id])}
                            helperText={marksValidationErrors[student.id]}
                            sx={{ width: 100 }}
                            inputProps={{
                              min: 0,
                              max: selectedExam.totalMarks ?? 100,
                            }}
                          />
                          <Typography
                            variant="caption"
                            color="text.secondary"
                            sx={{ ml: 1 }}
                          >
                            / {selectedExam.totalMarks}
                          </Typography>
                        </TableCell>
                        <TableCell>
                          {getMarksNumeric(student) !== null && selectedExam.totalMarks ? (
                            <Typography variant="body2">
                              {((getMarksNumeric(student) / selectedExam.totalMarks) * 100).toFixed(2)}%
                            </Typography>
                          ) : (
                            <Typography variant="body2" color="text.secondary">-</Typography>
                          )}
                        </TableCell>
                        <TableCell>
                          {student.grade ? (
                            <Chip
                              label={`${student.grade}${student.gradePoint ? ` (${student.gradePoint})` : ''}`}
                              size="small"
                              color="primary"
                              variant="outlined"
                            />
                          ) : (
                            <Typography variant="body2" color="text.secondary">-</Typography>
                          )}
                        </TableCell>
                        <TableCell>
                          {getMarksNumeric(student) !== null ? (
                            <Chip
                              label={
                                getMarksNumeric(student) >= (selectedExam.passingMarks ?? 35)
                                  ? "Pass"
                                  : "Fail"
                              }
                              size="small"
                              color={
                                getMarksNumeric(student) >= (selectedExam.passingMarks ?? 35)
                                  ? "success"
                                  : "error"
                              }
                            />
                          ) : (
                            <Chip
                              label="Pending"
                              size="small"
                              color="default"
                            />
                          )}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
              )}
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setMarkDialogOpen(false)} disabled={saveMarksLoading}>
            Cancel
          </Button>
          <Button
            variant="contained"
            onClick={handleSubmitMarks}
            disabled={saveMarksLoading || markDialogLoading || markDialogStudents.length === 0}
          >
            {saveMarksLoading ? (
              <>
                <CircularProgress size={20} sx={{ mr: 1 }} color="inherit" />
                Saving...
              </>
            ) : (
              "Save Marks"
            )}
          </Button>
        </DialogActions>
      </Dialog>

      {/* View Exam Dialog */}
      <Dialog
        open={viewDialogOpen}
        onClose={(e, reason) => {
          if (reason === "backdropClick" || reason === "escapeKeyDown") return;
          setViewDialogOpen(false);
        }}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>
          <Box sx={{ display: "flex", justifyContent: "space-between" }}>
            Exam Details
            <IconButton onClick={() => setViewDialogOpen(false)}>
              <Close />
            </IconButton>
          </Box>
        </DialogTitle>
        <DialogContent>
          {selectedExam && (
            <Box>
              <Box sx={{ textAlign: "center", mb: 3 }}>
                <Avatar
                  sx={{
                    width: 80,
                    height: 80,
                    mx: "auto",
                    mb: 1,
                    bgcolor: `${getExamTypeColor(selectedExam.examType)}.main`,
                  }}
                >
                  <Quiz sx={{ fontSize: 40 }} />
                </Avatar>
                <Typography variant="h6">{selectedExam.name}</Typography>
                <Chip
                  label={selectedExam.examType}
                  size="small"
                  color={getExamTypeColor(selectedExam.examType)}
                  sx={{ mt: 1 }}
                />
              </Box>
              <Divider sx={{ my: 2 }} />
              <Grid container spacing={2}>
                <Grid item xs={6}>
                  <Typography variant="caption" color="text.secondary">
                    Subject
                  </Typography>
                  <Typography variant="body2">
                    {selectedExam.subject}
                  </Typography>
                </Grid>
                <Grid item xs={6}>
                  <Typography variant="caption" color="text.secondary">
                    Class
                  </Typography>
                  <Typography variant="body2">{selectedExam.class || "—"}</Typography>
                </Grid>
                <Grid item xs={6}>
                  <Typography variant="caption" color="text.secondary">
                    Date
                  </Typography>
                  <Typography variant="body2">{selectedExam.date}</Typography>
                </Grid>
                <Grid item xs={6}>
                  <Typography variant="caption" color="text.secondary">
                    Time
                  </Typography>
                  <Typography variant="body2">{selectedExam.time}</Typography>
                </Grid>
                <Grid item xs={6}>
                  <Typography variant="caption" color="text.secondary">
                    Total Marks
                  </Typography>
                  <Typography variant="body2">
                    {selectedExam.totalMarks}
                  </Typography>
                </Grid>
                <Grid item xs={6}>
                  <Typography variant="caption" color="text.secondary">
                    Passing Marks
                  </Typography>
                  <Typography variant="body2">
                    {selectedExam.passingMarks}
                  </Typography>
                </Grid>
                <Grid item xs={12}>
                  <Typography variant="caption" color="text.secondary">
                    Marking Progress
                  </Typography>
                  <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                    <LinearProgress
                      variant="determinate"
                      value={
                        selectedExam.studentsCount
                          ? (selectedExam.markedCount / selectedExam.studentsCount) * 100
                          : 0
                      }
                      sx={{ flexGrow: 1, height: 10, borderRadius: 5 }}
                    />
                    <Typography variant="body2">
                      {selectedExam.markedCount ?? 0}/{selectedExam.studentsCount ?? 0}
                    </Typography>
                  </Box>
                </Grid>
              </Grid>
              <RecordMetadataSection record={selectedExam} />
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setViewDialogOpen(false)}>Close</Button>
          <Button
            variant="outlined"
            startIcon={<Download />}
            onClick={handleDownloadExamReport}
            disabled={!selectedExam?.id}
          >
            Download Report
          </Button>
          {selectedExam?.status !== "completed" && selectedExam?.status !== "graded" && (
            <Button
              variant="contained"
              startIcon={<Grade />}
              onClick={() => {
                setViewDialogOpen(false);
                setMarkDialogOpen(true);
              }}
            >
              Mark Scores
            </Button>
          )}
        </DialogActions>
      </Dialog>

      {/* Status update confirmation – center */}
      <Dialog
        open={statusConfirmOpen}
        onClose={(e, reason) => {
          if (reason === "backdropClick" || reason === "escapeKeyDown") return;
          closeStatusConfirm();
        }}
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle>
          {statusConfirmPayload.action === "completed"
            ? "Mark as Completed"
            : "Mark as Cancelled"}
        </DialogTitle>
        <DialogContent>
          <Typography variant="body2">
            {statusConfirmPayload.action === "completed"
              ? `Are you sure you want to mark "${statusConfirmPayload.examName}" as completed?`
              : `Are you sure you want to mark "${statusConfirmPayload.examName}" as cancelled?`}
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={closeStatusConfirm}>Cancel</Button>
          <Button variant="contained" onClick={handleStatusConfirm} color={statusConfirmPayload.action === "cancelled" ? "error" : "primary"}>
            Confirm
          </Button>
        </DialogActions>
      </Dialog>

      {/* Delete exam confirmation */}
      <Dialog open={deleteConfirmOpen} onClose={(e, reason) => { if (reason === "backdropClick" || reason === "escapeKeyDown") return; closeDeleteConfirm(); }} maxWidth="xs" fullWidth>
        <DialogTitle>Delete Exam</DialogTitle>
        <DialogContent>
          <Typography variant="body2">
            Are you sure you want to delete &quot;{examToDelete.name}&quot;? This action cannot be undone.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={closeDeleteConfirm} disabled={deleteLoading}>
            Cancel
          </Button>
          <Button variant="contained" color="error" onClick={handleDeleteConfirm} disabled={deleteLoading}>
            {deleteLoading ? (
              <>
                <CircularProgress size={20} sx={{ mr: 1 }} color="inherit" />
                Deleting...
              </>
            ) : (
              "Delete"
            )}
          </Button>
        </DialogActions>
      </Dialog>

      <Snackbar
        open={snackbar.open}
        autoHideDuration={4000}
        onClose={handleSnackbarClose}
        anchorOrigin={{ vertical: "top", horizontal: "right" }}
      >
        <Alert
          onClose={handleSnackbarClose}
          severity={snackbar.severity}
          variant="filled"
          sx={{ width: "100%" }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default ExamManagement;
